import { useCallback, useEffect, useState } from 'react';
import { buildInMemory, OpenAIEmbedder, EmbedError } from '@petitgen/remedy-core/browser';
import { useStore } from '../store.js';

const STORAGE_KEY = 'remedy:openai-key';

export function BuildView() {
  const { doc, source, build, setBuildState, errors } = useStore();
  const [apiKey, setApiKey] = useState<string>(() => sessionStorage.getItem(STORAGE_KEY) ?? '');
  const [model, setModel] = useState<string>('text-embedding-3-small');

  useEffect(() => {
    if (apiKey) sessionStorage.setItem(STORAGE_KEY, apiKey);
  }, [apiKey]);

  const canBuild = !!doc && errors.length === 0 && apiKey.trim().length > 0;

  const run = useCallback(async () => {
    if (!doc || !canBuild) return;
    setBuildState({
      status: 'running',
      progress: { done: 0, total: 0, path: '' },
      error: null,
    });
    try {
      const embedder = new OpenAIEmbedder({ apiKey: apiKey.trim(), model });
      const report = await buildInMemory({
        doc,
        source,
        embedder,
        prior: build.priorArtifact,
        onProgress: (done, total, path) =>
          setBuildState({ progress: { done, total, path } }),
      });
      setBuildState({
        status: 'done',
        artifact: report.artifact,
        priorArtifact: report.artifact,
        progress: null,
      });
    } catch (e) {
      const msg = e instanceof EmbedError ? `[${e.code}] ${e.message}` : (e as Error).message;
      setBuildState({ status: 'error', error: msg, progress: null });
    }
  }, [doc, source, apiKey, model, build.priorArtifact, canBuild, setBuildState]);

  const download = useCallback(() => {
    if (!build.artifact) return;
    const blob = new Blob([JSON.stringify(build.artifact, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '.remedy.compiled.json';
    a.click();
    URL.revokeObjectURL(url);
  }, [build.artifact]);

  if (!doc) {
    return (
      <div className="p-6 text-[13px] text-ink-500">Open a Remedy to run a build.</div>
    );
  }
  if (errors.length > 0) {
    return (
      <div className="p-6">
        <div className="mono rounded border border-status-deprecated/40 bg-status-deprecated/5 p-3 text-xs text-status-deprecated">
          Fix the {errors.length} schema error(s) in the Lint view before building.
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
      <div className="max-w-3xl">
        <h2 className="text-[14px] font-semibold text-ink-100">Compile a Remedy</h2>
        <p className="mt-1 text-[13px] text-ink-400">
          Runs parse → validate → embed via OpenAI → serialise. Embeddings are cached by
          (model, path, text) so a second run only re-embeds what changed. The compiled
          artifact is a SPEC §5.2 JSON document; download it, commit it, serve it.
        </p>

        <div className="mt-6 rounded-md border border-ink-800 bg-ink-900 p-4">
          <h3 className="label">OpenAI key (session only)</h3>
          <p className="mt-1 mb-3 text-[11px] text-ink-500">
            Stored in sessionStorage. Not sent anywhere except api.openai.com.
          </p>
          <input
            type="password"
            placeholder="sk-…"
            className="mono w-full rounded border border-ink-800 bg-ink-950 px-2.5 py-1.5 text-[12px] text-ink-100 placeholder:text-ink-600 focus:border-accent-500 focus:outline-none"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
          />
          <div className="mt-3 flex items-center gap-2">
            <label className="label">Model</label>
            <select
              className="mono rounded border border-ink-800 bg-ink-950 px-2 py-1 text-[12px] text-ink-100"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              <option value="text-embedding-3-small">text-embedding-3-small</option>
              <option value="text-embedding-3-large">text-embedding-3-large</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            className="mono rounded bg-accent-500 px-4 py-2 text-[12px] font-medium text-ink-950 hover:bg-accent-400 disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!canBuild || build.status === 'running'}
            onClick={run}
          >
            {build.status === 'running' ? 'Building…' : 'Run build'}
          </button>
          {build.artifact && (
            <button
              className="mono rounded border border-ink-700 bg-ink-900 px-4 py-2 text-[12px] text-ink-200 hover:border-accent-500 hover:text-accent-500"
              onClick={download}
            >
              Download .remedy.compiled.json
            </button>
          )}
        </div>

        {build.status === 'running' && build.progress && (
          <div className="mono mt-4 border-l-2 border-accent-500 bg-ink-900 p-3 text-xs">
            Embedding {build.progress.done}/{build.progress.total} —{' '}
            <span className="text-ink-500">{build.progress.path}</span>
          </div>
        )}
        {build.status === 'done' && build.artifact && (
          <div className="mono mt-4 border-l-2 border-status-deployed bg-ink-900 p-3 text-xs text-ink-200">
            Built. {Object.keys(build.artifact.embeddings).length} embeddings,{' '}
            model = {build.artifact.model}, source_hash ={' '}
            <span className="text-ink-500">{build.artifact.source_hash.slice(0, 20)}…</span>
          </div>
        )}
        {build.status === 'error' && (
          <div className="mono mt-4 border-l-2 border-status-deprecated bg-ink-900 p-3 text-xs text-status-deprecated">
            {build.error}
          </div>
        )}
      </div>
    </div>
  );
}
